/**
 * Inputs of the finance engine (docs/release-1/18 §3.1). The API builds one
 * `FinanceInput` per project from rows read under the viewer's RLS; the
 * engine is pure and never reads anything else.
 *
 * Days are project-local calendar dates (`YYYY-MM-DD`), which compare
 * correctly as strings.
 */
export type LocalDay = string;

export const BILLING_MODELS = ['hourly', 'fixed_fee', 'internal'] as const;
export type BillingModel = (typeof BILLING_MODELS)[number];

export const PRICING_MODES = ['none', 'hourly', 'fixed'] as const;
export type PricingMode = (typeof PRICING_MODES)[number];

export type RateSource = 'task' | 'parent' | 'stage' | 'project' | 'team';

/** A past own rate: applied up to and including `validUntil` (null = had none, inherit). */
export interface RatePeriod {
  validUntil: LocalDay;
  rateCents: number | null;
}

/** One rate owner (team default, project, stage or task): today's own rate + its past. */
export interface RateOwner {
  current: number | null;
  /** Any order; the engine sorts. */
  periods: readonly RatePeriod[];
}

export const NO_RATE: RateOwner = Object.freeze({ current: null, periods: [] });

export interface FinanceProjectInput {
  id: string;
  tracking: boolean;
  model: BillingModel;
  currency: string;
  /** The project's own rate (null = use the team default). */
  rate: RateOwner;
  /** The team default rate — the last link of the chain. */
  teamRate: RateOwner;
  /** Budget (hourly / internal) or the fee (fixed fee). */
  budgetCents: number | null;
  trustDefaultEstimates: boolean;
  /** `completed` when the project is done (fixed fee true-up). */
  completedDay: LocalDay | null;
}

export interface FinanceMilestoneInput {
  id: string;
  name: string;
  priceCents: number | null;
  rate: RateOwner;
  isCompleted: boolean;
  completedDay: LocalDay | null;
}

/** An explicit pricing row (no row = the project default). */
export interface TaskPricingInput {
  mode: PricingMode;
  priceCents: number | null;
}

export interface FinanceTaskInput {
  id: string;
  key: string;
  title: string;
  parentId: string | null;
  milestoneId: string | null;
  /** Workflow category: open | in_progress | review | done | cancelled … */
  statusCategory: string;
  archived: boolean;
  deleted: boolean;
  completedDay: LocalDay | null;
  laborHours: number;
  /** True once anyone changed the estimate (a LABOR_HOURS_UPDATED history row). */
  estimateEdited: boolean;
  assigneeId: string | null;
  respondentId: string | null;
  pricing: TaskPricingInput | null;
  /** The task's own hourly rate (current + past periods); kept apart from the
   *  pricing row so past rates survive going back to the project default. */
  rate: RateOwner;
}

/** Closed time aggregated per (task, person, project-local day). */
export interface FinanceAtom {
  taskId: string;
  userId: string;
  day: LocalDay;
  seconds: number;
}

export interface FinanceInput {
  project: FinanceProjectInput;
  milestones: readonly FinanceMilestoneInput[];
  tasks: readonly FinanceTaskInput[];
  atoms: readonly FinanceAtom[];
  /** Project-local today. */
  today: LocalDay;
}

/** Inclusive day window; `from: null` = since the beginning. */
export interface DayWindow {
  from: LocalDay | null;
  to: LocalDay;
}

export type UnitRef =
  | { kind: 'stage'; id: string }
  | { kind: 'task'; id: string }
  | { kind: 'project'; id: string };
